/**
 * Memory Graph — data layer (G1).
 *
 * Assembles a bounded, deterministic, honesty-preserving `MemoryGraphData` and
 * `InspectorModel` from the existing eight typed `/v1/knowledge/*` operations.
 * No new endpoint is introduced. Every server response is validated with the
 * same fail-closed manual parsing commitments.ts already uses (apps/web does not
 * depend on the query package, so the wire records are re-validated here).
 *
 * Honesty invariants (types.ts §8, README §8):
 * - Visibility never widens: the model carries no count, silhouette or hint of
 *   entities/relations the caller is not authorized to see. A relation whose
 *   endpoint the server did not return is dropped, not turned into a phantom
 *   node/edge. `truncated: true` only means more *authorized* nodes exist than
 *   the cap — it never quantifies unauthorized data.
 * - Missing/stale/failed coverage yields `status: "unknown"` with an `asOf`
 *   watermark; nodes may still render, but the status stays honest.
 * - Derived signals (status badges, clusters) never invent evidence; an
 *   `unknown` commitment never produces a badge. `asOf` is always the coverage
 *   watermark, never a wall clock.
 */

import { claimStates, entityTypes, relationTypes } from "@obsina/domain";
import {
  CONTROL_PLANE_REQUEST_TIMEOUT_MS,
  ControlPlaneError,
  getCommitmentEvidence,
  getOpenCommitments,
  normalizeApiBase,
} from "../api";
import { CsrfTokenError, csrfTokenCacheFor } from "../csrf";
import { parseKnowledgeCoverage } from "../commitments";
import type {
  CommitmentEvidence,
  CoverageDurability,
  CoverageExcludedLayer,
  KnowledgeAnswerStatus,
  KnowledgeCoverage,
  KnowledgeCoverageState,
  KnowledgeUnknownReason,
  OpenCommitment,
} from "../commitments";
import {
  CLUSTER_COLOR_ORDER,
  CLUSTER_MIN_HUB_DEGREE,
  MEMORY_GRAPH_MAX_CLUSTERS,
  MEMORY_GRAPH_MAX_EDGES,
  MEMORY_GRAPH_MAX_NODES,
  NODE_SIZE_TIER_MIN_DEGREE,
  NODE_STATUS_DUE_SOON_DAYS,
} from "./types";
import type {
  ClaimState,
  ClusterColorToken,
  EntityIdentifier,
  EntityStatus,
  EntityType,
  EvidenceKind,
  GraphCluster,
  GraphEdge,
  GraphNode,
  InspectorEvidenceRow,
  InspectorModel,
  InspectorOpenItem,
  MemoryGraphData,
  NarrowClusterCard,
  NarrowClusterEntity,
  NarrowVignetteDot,
  NodeSizeTier,
  NodeStatusBadge,
  RelationType,
  ResolvedMemoryGraph,
} from "./types";

/**
 * Upper bound on typed-op calls a single graph or inspector load may make.
 * The budget is spent by deterministic priority (evidence count desc, id asc);
 * exhausting it (or {@link MEMORY_GRAPH_MAX_NODES}) sets `truncated: true`.
 */
export const MEMORY_GRAPH_MAX_REQUESTS = 48;

/**
 * Maximum typed-op calls in flight at once within a single phase. The graph load
 * fans a phase's independent per-entity requests out concurrently up to this
 * bound (instead of one serial round-trip each) so opening the graph no longer
 * waits on ~one RTT per entity. Determinism is unaffected: results are collected
 * by input index and folded into the accumulator in the same deterministic order
 * as the serial version (see {@link mapWithConcurrency} and {@link loadMemoryGraph}).
 *
 * Twelve rather than eight because the relation traversal is the one phase still
 * spending a call per entity, and eight split the live tenant's eleven into two
 * waves for the sake of three requests. Measured on the volume harness (21
 * nodes, 43 edges, a 245 ms per-request floor): 1.90 s / 2.35 s at eight,
 * 1.53 s / 1.36 s at twelve. Browsers queue beyond their own per-origin limit,
 * so on a connection that cannot carry twelve this simply degrades to the old
 * behavior rather than failing.
 */
export const MEMORY_GRAPH_FETCH_CONCURRENCY = 12;

/**
 * Bounded first page for the relation traversal around a single entity. This is
 * an evidence-returning read, so it stays on the shared 50-record ceiling: what
 * bounds it is the audit event that has to record every pointer it releases.
 */
const RELATIONS_PAGE_SIZE = 50;

/**
 * Page size for the two enumeration reads, matching the API's own enumeration
 * ceiling. They release counts rather than pointers, so the audit bound that
 * pins {@link RELATIONS_PAGE_SIZE} does not apply and the only thing a small
 * page buys is more serial round trips: the keyset chain cannot be run in
 * parallel (each cursor is issued by the page before it), so at fifty a page the
 * live tenant's 460 relations cost ten sequential requests. At 250 the entity
 * chain is one request — the node cap is 240 — and the relation chain is two.
 */
const ENUMERATION_PAGE_SIZE = 250;

/**
 * Ids per `get_entities` call, matching the API boundary's own batch ceiling.
 * The graph resolves its entities in batches of this size instead of one call
 * each: the fixed cost of a request — transport, session, CSRF, the coverage
 * preamble — was larger than the entity read it carried, and a load spent 33 of
 * them where 14 carry the same map.
 */
const ENTITY_BATCH_SIZE = 50;

const DAY_MS = 24 * 60 * 60 * 1000;

/* ------------------------------------------------------------------------- *
 * Fail-closed wire validation. The record primitives below are reproduced from
 * commitments.ts (they are not exported there); the *coverage* block is not —
 * it is imported, because two copies of one gate is how this surface came to
 * read a state the API had already stopped sending.
 * ------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function invalid(): never {
  throw new ControlPlaneError(
    "The knowledge surface returned an invalid response.",
  );
}

function requireString(value: unknown): string {
  if (typeof value !== "string") invalid();
  return value;
}

function nullableString(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== "string") invalid();
  return value;
}

function answerStatus(value: unknown): KnowledgeAnswerStatus {
  if (value === "ok" || value === "unknown") return value;
  return invalid();
}

function unknownReason(value: unknown): KnowledgeUnknownReason | null {
  if (value === null) return null;
  if (value === "coverage_unknown" || value === "no_authorized_evidence") {
    return value;
  }
  return invalid();
}

const ENTITY_TYPE_SET: ReadonlySet<string> = new Set(entityTypes);
const RELATION_TYPE_SET: ReadonlySet<string> = new Set(relationTypes);
const CLAIM_STATE_SET: ReadonlySet<string> = new Set(claimStates);
const ENTITY_STATUS_SET: ReadonlySet<string> = new Set([
  "active",
  "merged",
  "tombstoned",
] satisfies readonly EntityStatus[]);

function requireEntityType(value: unknown): EntityType {
  const kind = requireString(value);
  if (!ENTITY_TYPE_SET.has(kind)) invalid();
  return kind as EntityType;
}

/**
 * How much the system itself stands behind a relation, as the API stated it.
 *
 * Three answers, and they are not interchangeable:
 *
 * - **Absent** → `undefined`. The responder never stated it (an API released
 *   before the field existed), so this reader makes no claim either. It must
 *   never become `supported`: that would be this surface inventing confidence
 *   the record does not carry, which is the whole defect this reads for.
 * - **A value in the frozen vocabulary** → carried through verbatim.
 * - **A value outside it** → the response is rejected. `ClaimState` is frozen in
 *   `@obsina/domain` and both sides import that same union, so a value outside
 *   it means responder and reader disagree about what an assertion state *is*.
 *   Dropping it would be the worse failure: an unreadable state would render as
 *   an ordinary relation, which is exactly the silence being fixed.
 */
function optionalClaimState(value: unknown): ClaimState | undefined {
  if (value === undefined || value === null) return undefined;
  const state = requireString(value);
  if (!CLAIM_STATE_SET.has(state)) invalid();
  return state as ClaimState;
}

/** The entity counterpart of {@link optionalClaimState}; same three answers. */
function optionalEntityStatus(value: unknown): EntityStatus | undefined {
  if (value === undefined || value === null) return undefined;
  const status = requireString(value);
  if (!ENTITY_STATUS_SET.has(status)) invalid();
  return status as EntityStatus;
}

interface KnowledgeEnvelope {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly data: unknown;
  readonly coverage: KnowledgeCoverage;
  readonly nextCursor: string | null;
}

function parseKnowledgeEnvelope(value: unknown): KnowledgeEnvelope {
  if (!isRecord(value)) invalid();
  return {
    status: answerStatus(value.status),
    reason: unknownReason(value.reason),
    data: value.data ?? null,
    coverage: parseKnowledgeCoverage(value.coverage),
    nextCursor: nullableString(value.nextCursor),
  };
}

/**
 * What the map needs from an entity: identity, and how much evidence stands
 * behind it. The graph never draws evidence, only sizes and orders by its
 * amount, so this is the whole of what the multi-record reads return.
 */
interface ParsedEntity {
  readonly entityId: string;
  readonly entityType: EntityType;
  readonly canonicalName: string;
  /** See {@link EntityIdentifier}; absent when the surface named none. */
  readonly identifier?: EntityIdentifier;
  readonly identifierCount?: number;
  /**
   * Record lifecycle as the API stated it, or undefined when it stated none.
   * A merged or tombstoned entity is still a record the system holds, so it is
   * still drawn — but it may not be drawn as a live one, and the view can only
   * say so if this reaches it.
   */
  readonly status?: EntityStatus;
  /** Count of authorized evidence pointers on the entity record. */
  readonly evidenceCount: number;
  /**
   * True when the record carries a bounded first page of evidence rather than
   * the whole set, so {@link evidenceCount} is a lower bound. A hub entity can
   * hold several times the server's per-record bound.
   */
  readonly evidenceTruncated: boolean;
}

/** The single-entity read additionally carries the pointers themselves. */
interface ParsedEntityDetail extends ParsedEntity {
  /** Evidence ids, used by the inspector's bounded first page. */
  readonly evidenceIds: readonly string[];
}

function evidencePointerId(value: unknown): string {
  if (!isRecord(value)) invalid();
  return requireString(value.evidenceId);
}

function requireCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    invalid();
  }
  return value;
}

function parseEntityRecord(value: unknown): ParsedEntityDetail {
  if (!isRecord(value)) invalid();
  const evidence = Array.isArray(value.evidence) ? value.evidence : invalid();
  return {
    entityId: requireString(value.entityId),
    entityType: requireEntityType(value.entityType),
    canonicalName: requireString(value.canonicalName),
    ...statusOf(value),
    ...identifierOf(value),
    evidenceCount: evidence.length,
    evidenceIds: evidence.map(evidencePointerId),
    // Only an explicit `true` claims a shortfall. A reader must never invent
    // one it was not told about: an absent field means the responder did not
    // bound this list, and rendering "100+" over a complete set of 100 would be
    // its own false statement.
    evidenceTruncated: value.evidenceTruncated === true,
  };
}

/**
 * How many authorized evidence pointers a multi-record row stands on.
 *
 * The multi-record reads state this as a count; the single-record reads hand
 * back the pointers. Both are read here because the two halves of this system
 * ship separately: the web bundle publishes on push while the API is released
 * by hand, so a new bundle spends that window talking to a server that still
 * sends arrays. Accepting either is what keeps the graph drawn across it.
 *
 * Neither present is invalid, never zero: a silent zero would shrink a hub to a
 * speck and reorder the entire traversal behind it.
 */
function requireEvidenceCount(value: Record<string, unknown>): number {
  if (value.evidenceCount !== undefined)
    return requireCount(value.evidenceCount);
  if (Array.isArray(value.evidence)) return value.evidence.length;
  return invalid();
}

/**
 * The multi-record shape (`get_entities`, `list_entities`): the same identity,
 * with the evidence stated as a count instead of handed over.
 */
function parseEntitySummaryRecord(value: unknown): ParsedEntity {
  if (!isRecord(value)) invalid();
  return {
    entityId: requireString(value.entityId),
    entityType: requireEntityType(value.entityType),
    canonicalName: requireString(value.canonicalName),
    ...statusOf(value),
    ...identifierOf(value),
    evidenceCount: requireEvidenceCount(value),
    evidenceTruncated: value.evidenceTruncated === true,
  };
}

/**
 * Spreads the entity's lifecycle in only when the responder stated one. An
 * explicit `status: undefined` key would be indistinguishable from a stated
 * value under `exactOptionalPropertyTypes`, so the key is omitted instead —
 * "not read" has to stay absent, not present-and-empty.
 */
function statusOf(value: Record<string, unknown>): { status?: EntityStatus } {
  const status = optionalEntityStatus(value.status);
  return status === undefined ? {} : { status };
}

/**
 * The distinguishing identifier, spread in only when the responder named one.
 *
 * Additive and unrecognized-means-absent, the same rule the layer label and the
 * unreadable reason follow: this value is only ever printed beside a name, so a
 * kind this build does not know produces nothing rather than a label it cannot
 * describe. A record that states `identifier: null` legitimately has none, and
 * that is absent here too — the surface has nothing to draw either way.
 *
 * The count is clamped to at least one whenever an identifier is named: a
 * surface that says "one of 0" would be describing a set that does not contain
 * what it is showing.
 */
function identifierOf(value: Record<string, unknown>): {
  identifier?: EntityIdentifier;
  identifierCount?: number;
} {
  const raw = value.identifier;
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) return {};
  const record = raw as Record<string, unknown>;
  if (record.kind !== "email_address" || typeof record.value !== "string") {
    return {};
  }
  const trimmed = record.value.trim();
  if (trimmed === "") return {};
  const stated = value.identifierCount;
  const count =
    typeof stated === "number" && Number.isInteger(stated) && stated > 0
      ? stated
      : 1;
  return {
    identifier: { kind: "email_address", value: trimmed },
    identifierCount: count,
  };
}

interface ParsedRelation {
  readonly relationId: string;
  readonly fromEntityId: string;
  readonly toEntityId: string;
  /**
   * The relation's kind, or null when the responder stated a code outside the
   * frozen vocabulary this build carries. See {@link parseRelationIdentity}:
   * null means "this build cannot name this link", never "there is no link".
   */
  readonly relationType: RelationType | null;
  /**
   * How strongly the system asserts this relation, as the API stated it, or
   * undefined when it stated none. See {@link optionalClaimState}: an uncertain
   * or conflicted relation must not reach the canvas looking like a supported
   * one.
   */
  readonly state?: ClaimState;
  readonly evidenceCount: number;
  /**
   * How many distinct published documents attest this bond, as the API stated
   * it, or undefined when it stated none. See {@link optionalObservationCount}:
   * absent means "not computed", never "observed once", so an edge only uses it
   * when every relation merged into it carries one.
   */
  readonly observationCount?: number;
}

/**
 * Parses a relation record. A relation type outside the frozen vocabulary keeps
 * the relation and loses only its name (see {@link parseRelationIdentity}).
 */
function parseRelationRecord(value: unknown): ParsedRelation {
  if (!isRecord(value)) invalid();
  const evidence = Array.isArray(value.evidence) ? value.evidence : invalid();
  // Every evidence element is validated fail-closed before it may count
  // toward an edge weight; a malformed pointer rejects the whole response.
  const evidenceIds = evidence.map(evidencePointerId);
  return parseRelationIdentity(value, evidenceIds.length);
}

/**
 * The enumeration shape (`list_relations`): the edge, with the evidence behind
 * it stated as a count. Same fail-closed rule as
 * {@link parseEntitySummaryRecord} — the count carries the edge weight, so an
 * absent one is an invalid response rather than a weightless edge.
 */
function parseRelationSummaryRecord(value: unknown): ParsedRelation {
  if (!isRecord(value)) invalid();
  return parseRelationIdentity(value, requireEvidenceCount(value));
}

/**
 * A relation whose type this build does not carry keeps its place on the map and
 * loses only its name.
 *
 * This branch used to drop the whole record. That was the one loss on this
 * surface nobody could see: an authorized, evidenced link between two drawn
 * entities vanished, and neither the map, the panel nor the coverage strip said
 * a line was missing. "No silent skip" is a product invariant here, and a
 * missing edge is not merely quieter than a wrong one — it is a false statement
 * about the network, because the map's whole claim is that a line is drawn
 * wherever an authorized relation exists.
 *
 * Both halves of this system ship separately (the web bundle publishes on push,
 * the API is released by hand), so a vocabulary the server has already grown
 * into is exactly the case this reaches. `null` is therefore *not* the absence
 * of a relation: it is a relation whose kind this build cannot name, and every
 * surface that names a relation says so in plain words — "Linked" on the map and
 * in the panel, "is linked to" in the sentence — rather than inventing a verb or
 * printing the raw code. The forward-compatible, fail-closed part is preserved:
 * nothing is asserted about what the link means, only that it exists.
 */
function parseRelationIdentity(
  value: Record<string, unknown>,
  evidenceCount: number,
): ParsedRelation {
  const relationType = requireString(value.relationType);
  const relationId = requireString(value.relationId);
  const fromEntityId = requireString(value.fromEntityId);
  const toEntityId = requireString(value.toEntityId);
  const state = optionalClaimState(value.state);
  const observationCount = optionalObservationCount(value.observationCount);
  return {
    relationId,
    fromEntityId,
    toEntityId,
    relationType: RELATION_TYPE_SET.has(relationType)
      ? (relationType as RelationType)
      : null,
    ...(state === undefined ? {} : { state }),
    ...(observationCount === undefined ? {} : { observationCount }),
    evidenceCount,
  };
}

/**
 * How often the bond behind this relation has been observed, when the API says.
 *
 * Absent and `null` both mean "not computed yet" — the strength is rebuilt on a
 * scheduler tick, and an older API sends nothing at all — so both omit the key
 * rather than standing in a number. That distinction is the whole point: a
 * missing strength read as 1 would draw the busiest correspondence in the
 * tenant as the faintest line on the map.
 *
 * A stated value is validated fail-closed, on the same terms as every other
 * count here. Zero is invalid, not weak: a bond nothing attests has no stored
 * strength, so a zero is a response contradicting the record it came from.
 */
function optionalObservationCount(value: unknown): number | undefined {
  if (value === undefined || value === null) return undefined;
  if (requireCount(value) < 1) invalid();
  return value as number;
}

/* ------------------------------------------------------------------------- *
 * Bounded, credentialed POST for the two knowledge ops without an api.ts
 * helper (get_entity, get_relations). Same CSRF + cookie + timeout behavior as
 * api.ts's private `request`; api.ts is read-only here and cannot be widened.
 * ------------------------------------------------------------------------- */

function responseError(response: Response): ControlPlaneError {
  const requestId = response.headers.get("x-request-id");
  return new ControlPlaneError(
    `The control plane returned ${response.status}.${
      requestId ? ` Request ${requestId}.` : ""
    }`,
    response.status,
  );
}

async function boundedKnowledgeFetch(
  input: string,
  init: RequestInit,
  signal?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const forwardAbort = () => {
    controller.abort(
      signal?.reason ??
        new DOMException("The request was aborted.", "AbortError"),
    );
  };
  if (signal) {
    if (signal.aborted) forwardAbort();
    else signal.addEventListener("abort", forwardAbort, { once: true });
  }
  const timeout = globalThis.setTimeout(() => {
    controller.abort(
      new ControlPlaneError("The control-plane request timed out. Try again."),
    );
  }, CONTROL_PLANE_REQUEST_TIMEOUT_MS);
  try {
    return await fetch(input, {
      ...init,
      credentials: "include",
      signal: controller.signal,
    });
  } finally {
    globalThis.clearTimeout(timeout);
    signal?.removeEventListener("abort", forwardAbort);
  }
}

async function knowledgePost(
  apiBase: string,
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<unknown> {
  const origin = normalizeApiBase(apiBase);
  if (!origin) {
    throw new ControlPlaneError("The control-plane API origin is invalid.");
  }
  const payload = JSON.stringify(body);
  // A graph load fans out dozens of these; before the shared cache each one
  // paid its own `/v1/security/csrf` round trip first, doubling the surface.
  const response = await sendWithCsrf(origin, (csrf) =>
    boundedKnowledgeFetch(
      `${origin}${path}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "csrf-token": csrf,
        },
        body: payload,
      },
      signal,
    ),
  );
  if (!response.ok) throw responseError(response);
  return response.json();
}

async function sendWithCsrf(
  origin: string,
  perform: (csrf: string) => Promise<Response>,
): Promise<Response> {
  try {
    return await csrfTokenCacheFor(origin).send(perform);
  } catch (error) {
    if (error instanceof CsrfTokenError) {
      throw new ControlPlaneError(error.message, error.status);
    }
    throw error;
  }
}

interface EntityResult {
  readonly entity: ParsedEntityDetail | null;
  readonly coverage: KnowledgeCoverage;
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
}

interface EntityBatchResult {
  readonly entities: readonly ParsedEntity[];
  readonly coverage: KnowledgeCoverage;
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
}

/**
 * One `get_entities` call: the same authorized read as {@link fetchEntity},
 * folded over a bounded id set. An id the caller may not read is simply absent
 * from `entities` — the batch reports no per-id reason, exactly as the single
 * read cannot distinguish "withheld" from "absent".
 */
async function fetchEntityBatch(
  apiBase: string,
  entityIds: readonly string[],
  asOf: string,
  signal?: AbortSignal,
): Promise<EntityBatchResult> {
  const payload = await knowledgePost(
    apiBase,
    "/v1/knowledge/get_entities",
    { asOf, entityIds: [...entityIds] },
    signal,
  );
  const envelope = parseKnowledgeEnvelope(payload);
  const entities: ParsedEntity[] = [];
  if (envelope.data !== null) {
    if (!isRecord(envelope.data) || !Array.isArray(envelope.data.items)) {
      invalid();
    }
    for (const raw of envelope.data.items) {
      entities.push(parseEntitySummaryRecord(raw));
    }
  }
  return {
    entities,
    coverage: envelope.coverage,
    status: envelope.status,
    reason: envelope.reason,
  };
}

async function fetchEntity(
  apiBase: string,
  entityId: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<EntityResult> {
  const payload = await knowledgePost(
    apiBase,
    "/v1/knowledge/get_entity",
    { entityId, asOf },
    signal,
  );
  const envelope = parseKnowledgeEnvelope(payload);
  return {
    entity: envelope.data === null ? null : parseEntityRecord(envelope.data),
    coverage: envelope.coverage,
    status: envelope.status,
    reason: envelope.reason,
  };
}

/**
 * One seed's entity read, isolated from the rest of the fan-out.
 *
 * A graph load resolves dozens of entities, and every one of them used to be
 * able to end the whole load: one `get_entity` rejection propagated out of the
 * batch and the reader saw an error card instead of the map. Observed on live
 * staging, where two over-evidenced people made the entire graph unreachable
 * while thirty-nine other entities were answering normally.
 *
 * A dropped entity is never silent. The caller marks the view `truncated`, the
 * same signal the node and request caps use, so the map states that more
 * authorized data exists than it draws. Fail-closed is preserved: an entity
 * that did not resolve becomes no node, and `buildEdges` drops any edge with an
 * unresolved endpoint, so nothing is ever drawn on a record this reader could
 * not verify.
 *
 * Two failures still propagate, because for them a partial map would hide the
 * only thing worth saying: an abort (the caller has moved on and wants no
 * result at all) and an authentication or authorization failure (the surface as
 * a whole is closed to this reader — a half-drawn map would read as "this is
 * what you may see" instead of "your session ended").
 */
async function fetchSeedEntity(
  apiBase: string,
  entityId: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<EntityResult | null> {
  try {
    return await fetchEntity(apiBase, entityId, asOf, signal);
  } catch (error) {
    if (isAbort(error)) throw error;
    if (
      error instanceof ControlPlaneError &&
      (error.status === 401 || error.status === 403)
    ) {
      throw error;
    }
    return null;
  }
}

function isAbort(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

/** The three coverage-bearing fields of any envelope, kept for a fixed-order fold. */
interface EnvelopeStatus {
  readonly coverage: KnowledgeCoverage;
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
}

interface ChunkResolution {
  /** Records to insert, in the chunk's (id asc) input order. */
  readonly entities: readonly ParsedEntity[];
  /** Envelopes to fold, in the order the serial reader would have folded them. */
  readonly envelopes: readonly EnvelopeStatus[];
  /** True when at least one id in the chunk could not be read at all. */
  readonly dropped: boolean;
  /** Round trips actually spent, so the request budget stays truthful. */
  readonly requests: number;
}

/**
 * Resolves one chunk of entity ids, preferring the single batched read and
 * falling back to the per-id reads it replaces.
 *
 * The fallback is not defensive padding: it is the behavior this surface was
 * proven to need. A single over-evidenced entity used to fail its own read, and
 * because each id was fetched separately the other thirty-nine still drew a map.
 * A batch has no such isolation — one bad record fails the whole call — so a
 * failed batch retries the chunk id by id, and the map survives exactly as it
 * did before. Abort and 401/403 still propagate untouched: the first means the
 * caller wants no result, the second that the surface is closed, and a
 * half-drawn map would misstate both.
 */
async function resolveEntityChunk(
  apiBase: string,
  entityIds: readonly string[],
  asOf: string,
  signal?: AbortSignal,
): Promise<ChunkResolution> {
  try {
    const batch = await fetchEntityBatch(apiBase, entityIds, asOf, signal);
    return {
      entities: batch.entities,
      envelopes: [
        {
          coverage: batch.coverage,
          status: batch.status,
          reason: batch.reason,
        },
      ],
      // An id absent from the batch resolved to nothing or is withheld — the
      // same honest empty a null single read is, and not a stated gap.
      dropped: false,
      requests: 1,
    };
  } catch (error) {
    if (isAbort(error)) throw error;
    if (
      error instanceof ControlPlaneError &&
      (error.status === 401 || error.status === 403)
    ) {
      throw error;
    }
    const results = await mapWithConcurrency(
      entityIds,
      MEMORY_GRAPH_FETCH_CONCURRENCY,
      (id) => fetchSeedEntity(apiBase, id, asOf, signal),
    );
    const entities: ParsedEntity[] = [];
    const envelopes: EnvelopeStatus[] = [];
    let dropped = false;
    for (const result of results) {
      if (result === null) {
        dropped = true;
        continue;
      }
      envelopes.push({
        coverage: result.coverage,
        status: result.status,
        reason: result.reason,
      });
      if (result.entity) entities.push(result.entity);
    }
    return {
      entities,
      envelopes,
      dropped,
      requests: 1 + entityIds.length,
    };
  }
}

/**
 * Resolves a whole id set in batches, in input (id asc) order. Chunks run
 * concurrently but are folded by index, so the coverage sequence and the entity
 * insertion order are the same ones the serial reader produced.
 */
async function resolveEntities(
  apiBase: string,
  entityIds: readonly string[],
  asOf: string,
  signal?: AbortSignal,
): Promise<ChunkResolution> {
  const chunks: string[][] = [];
  for (let at = 0; at < entityIds.length; at += ENTITY_BATCH_SIZE) {
    chunks.push([...entityIds.slice(at, at + ENTITY_BATCH_SIZE)]);
  }
  const resolved = await mapWithConcurrency(
    chunks,
    MEMORY_GRAPH_FETCH_CONCURRENCY,
    (chunk) => resolveEntityChunk(apiBase, chunk, asOf, signal),
  );
  return {
    entities: resolved.flatMap((chunk) => chunk.entities),
    envelopes: resolved.flatMap((chunk) => chunk.envelopes),
    dropped: resolved.some((chunk) => chunk.dropped),
    requests: resolved.reduce((sum, chunk) => sum + chunk.requests, 0),
  };
}

interface RelationsResult {
  readonly relations: readonly ParsedRelation[];
  readonly coverage: KnowledgeCoverage;
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly nextCursor: string | null;
}

async function fetchRelations(
  apiBase: string,
  entityId: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<RelationsResult> {
  const payload = await knowledgePost(
    apiBase,
    "/v1/knowledge/get_relations",
    {
      entityId,
      asOf,
      relationTypes: [...relationTypes],
      direction: "both",
      limit: RELATIONS_PAGE_SIZE,
      cursor: null,
    },
    signal,
  );
  const envelope = parseKnowledgeEnvelope(payload);
  const relations: ParsedRelation[] = [];
  if (envelope.data !== null) {
    if (!isRecord(envelope.data) || !Array.isArray(envelope.data.items)) {
      invalid();
    }
    for (const raw of envelope.data.items) {
      relations.push(parseRelationRecord(raw));
    }
  }
  return {
    relations,
    coverage: envelope.coverage,
    status: envelope.status,
    reason: envelope.reason,
    nextCursor: envelope.nextCursor,
  };
}

interface EntitiesPageResult {
  readonly entities: readonly ParsedEntity[];
  readonly coverage: KnowledgeCoverage;
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly nextCursor: string | null;
}

/**
 * One keyset page of the tenant's authorized entities (list_entities). Used only
 * by the full-network view; the response is re-validated fail-closed exactly like
 * every other op, and an unresolved/withheld entity is simply absent — the page
 * carries no silhouette of anything the caller is not authorized to see.
 */
async function fetchEntitiesPage(
  apiBase: string,
  asOf: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<EntitiesPageResult> {
  const payload = await knowledgePost(
    apiBase,
    "/v1/knowledge/list_entities",
    { asOf, limit: ENUMERATION_PAGE_SIZE, cursor },
    signal,
  );
  const envelope = parseKnowledgeEnvelope(payload);
  const entities: ParsedEntity[] = [];
  if (envelope.data !== null) {
    if (!isRecord(envelope.data) || !Array.isArray(envelope.data.items)) {
      invalid();
    }
    for (const raw of envelope.data.items) {
      entities.push(parseEntitySummaryRecord(raw));
    }
  }
  return {
    entities,
    coverage: envelope.coverage,
    status: envelope.status,
    reason: envelope.reason,
    nextCursor: envelope.nextCursor,
  };
}

/**
 * One keyset page of the tenant's authorized relations of every type
 * (list_relations). Same fail-closed re-validation as {@link fetchRelations}; an
 * edge whose type is outside the frozen vocabulary is drawn without a name, not
 * dropped (see {@link parseRelationIdentity}). This is the read that reaches
 * that case: the per-entity traversal asks the server for the vocabulary this
 * build carries, while the enumeration asks for everything the caller may see.
 */
async function fetchRelationsPage(
  apiBase: string,
  asOf: string,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<RelationsResult> {
  const payload = await knowledgePost(
    apiBase,
    "/v1/knowledge/list_relations",
    { asOf, limit: ENUMERATION_PAGE_SIZE, cursor },
    signal,
  );
  const envelope = parseKnowledgeEnvelope(payload);
  const relations: ParsedRelation[] = [];
  if (envelope.data !== null) {
    if (!isRecord(envelope.data) || !Array.isArray(envelope.data.items)) {
      invalid();
    }
    for (const raw of envelope.data.items) {
      relations.push(parseRelationSummaryRecord(raw));
    }
  }
  return {
    relations,
    coverage: envelope.coverage,
    status: envelope.status,
    reason: envelope.reason,
    nextCursor: envelope.nextCursor,
  };
}

/**
 * A `get_relations` or `get_entity` envelope is `unknown / no_authorized_evidence`
 * whenever the frozen query/api layer resolves zero authorized rows for the
 * entity — its fail-closed default, because "empty" and "withheld" are
 * indistinguishable there. For the graph view, an entity whose coverage is
 * already *complete* (web state `"ok"`) but which simply has no authorized
 * relations of its own, or no authorized entity claim, is an honest empty result,
 * not an unknown answer: many seeds and neighbors hit this path (a commitment
 * subject with no relation, or an edge's counterparty entity the caller cannot
 * claim), and recording each as unknown would flip the whole view to the unknown
 * surface and hide every real node/edge the other seeds do carry. Such an
 * envelope is folded into the coverage watermark as `ok` (its `asOf` still counts
 * toward the min); the row itself is still dropped fail-closed — an unresolved
 * entity becomes no node, and {@link buildEdges} drops any edge with an
 * unresolved endpoint, so visibility never widens. Every other unknown still
 * turns the view unknown: a `coverage_unknown` envelope, a
 * `no_authorized_evidence` one whose coverage is itself incomplete, and any
 * unknown from get_open_commitments (fail-closed preserved). Callers that must
 * stay unknown when the primary subject cannot resolve (the inspector's focused
 * entity) deliberately record the raw status instead of this fold.
 */
function honestEmptyCoverageStatus(result: {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly coverage: KnowledgeCoverage;
}): {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
} {
  if (
    result.status === "unknown" &&
    result.reason === "no_authorized_evidence" &&
    // `partial` counts as established here for the same reason `complete` does:
    // it means every required source reconciled through `asOf` and only a
    // derived layer over their content is unread. The basis this entity's
    // emptiness is judged against holds either way. Only `unknown` — where the
    // basis itself is not established — still turns the whole view unknown.
    result.coverage.state !== "unknown"
  ) {
    return { status: "ok", reason: null };
  }
  return { status: result.status, reason: result.reason };
}

/* ------------------------------------------------------------------------- *
 * Coverage watermark accumulation across the consulted envelopes.
 * ------------------------------------------------------------------------- */

interface AggregateCoverage {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly asOf: string | null;
  readonly coverage: KnowledgeCoverage;
}

/**
 * Folds the coverage of every consulted envelope into one honest watermark:
 * `asOf` is the MIN of all `coverage.asOf` values, and the whole view is
 * `unknown` if any single envelope was unknown (carrying that first unknown
 * envelope's reason and coverage detail). Evidence responses carry no coverage
 * and are recorded for status only.
 *
 * Durability is one of the two fields folded across *all* envelopes rather than
 * taken from the representative one, and only ever in the conservative
 * direction: a terminal envelope plus a transient one is `mixed`, so the view
 * never tells a reader to stop waiting for work that is in fact still queued,
 * nor promises that waiting will clear a permanent boundary. One envelope that
 * states no durability makes the fold state none either — a partial claim about
 * the whole view is worse than no claim.
 *
 * The coverage *state* is the other, and folds worst-wins: one `unknown`
 * envelope makes the view unknown, one `partial` among otherwise complete ones
 * makes it partial. Taking it from the representative envelope instead would
 * let a complete first page speak for a partial later one and publish a network
 * as fully read when it is not.
 */
class CoverageAccumulator {
  private base: KnowledgeCoverage | null = null;
  private minAsOf: string | null = null;
  private sawTerminal = false;
  private sawTransient = false;
  private sawUnstated = false;
  private sawPartialCoverage = false;
  private sawUnknownCoverage = false;
  /**
   * The first partial envelope, so a partial fold reports the shortfall that
   * actually exists (its reasons and its media/knowledge counts) rather than the
   * empty reason list of whichever complete envelope happened to arrive first.
   */
  private firstPartial: KnowledgeCoverage | null = null;
  private firstUnknown: {
    readonly coverage: KnowledgeCoverage;
    readonly reason: KnowledgeUnknownReason | null;
  } | null = null;

  record(
    coverage: KnowledgeCoverage | null,
    status: KnowledgeAnswerStatus,
    reason: KnowledgeUnknownReason | null,
  ): void {
    if (coverage) {
      this.base ??= coverage;
      if (
        this.minAsOf === null ||
        Date.parse(coverage.asOf) < Date.parse(this.minAsOf)
      ) {
        this.minAsOf = coverage.asOf;
      }
      this.recordDurability(coverage.durability);
      for (const layer of coverage.excludedLayers) this.excluded.add(layer);
      if (coverage.state === "unknown") this.sawUnknownCoverage = true;
      else if (coverage.state === "partial") {
        this.sawPartialCoverage = true;
        this.firstPartial ??= coverage;
      }
    }
    if (status === "unknown" && !this.firstUnknown) {
      this.firstUnknown = {
        coverage: coverage ?? this.base ?? fallback(),
        reason,
      };
    }
  }

  private recordDurability(durability: CoverageDurability | null): void {
    switch (durability) {
      case null:
        this.sawUnstated = true;
        return;
      case "terminal":
        this.sawTerminal = true;
        return;
      case "transient":
        this.sawTransient = true;
        return;
      case "mixed":
        this.sawTerminal = true;
        this.sawTransient = true;
        return;
      case "settled":
        // Carries no reason at all, so it adds nothing to the fold.
        return;
    }
  }

  private durability(): CoverageDurability | null {
    if (this.sawUnstated) return null;
    if (this.sawTerminal && this.sawTransient) return "mixed";
    if (this.sawTerminal) return "terminal";
    if (this.sawTransient) return "transient";
    return "settled";
  }

  get anyUnknown(): boolean {
    return this.firstUnknown !== null;
  }

  /** Union over every envelope; see the fold in {@link build}. */
  private readonly excluded = new Set<CoverageExcludedLayer>();

  /**
   * Worst-wins over every recorded envelope, never the representative's value.
   * A load that recorded no coverage at all is `unknown`, not `complete`: an
   * unestablished basis is the one thing "nothing was recorded" cannot mean.
   */
  private coverageState(): KnowledgeCoverageState {
    if (this.base === null || this.sawUnknownCoverage) return "unknown";
    return this.sawPartialCoverage ? "partial" : "complete";
  }

  build(): AggregateCoverage {
    const representative =
      this.firstUnknown?.coverage ??
      this.firstPartial ??
      this.base ??
      fallback();
    const asOf = this.minAsOf ?? representative.asOf;
    return {
      status: this.anyUnknown ? "unknown" : "ok",
      reason: this.firstUnknown?.reason ?? null,
      asOf,
      coverage: {
        state: this.coverageState(),
        asOf,
        requiredSourceCount: representative.requiredSourceCount,
        reconciledSourceCount: representative.reconciledSourceCount,
        reasons: representative.reasons,
        durability: this.durability(),
        media: representative.media,
        knowledge: representative.knowledge,
        // Folded across every envelope rather than taken from the
        // representative: an exclusion is a property of the build, so one read
        // that states it states it for the view. Taking it from whichever
        // envelope happened to be representative would drop the declaration
        // whenever that one came from an older responder.
        excludedLayers: [...this.excluded].sort(),
      },
    };
  }
}

/** Unreachable in practice: every load records at least one coverage first. */
function fallback(): KnowledgeCoverage {
  return {
    state: "unknown",
    asOf: new Date(0).toISOString(),
    requiredSourceCount: 0,
    reconciledSourceCount: 0,
    reasons: [],
    durability: null,
    excludedLayers: [],
    media: null,
    knowledge: null,
  };
}

/**
 * Turns the folded coverage plus an assembled network into the view envelope.
 *
 * An unknown fold drops the assembled nodes/edges/clusters instead of shipping
 * them beside an `unknown` status: nothing was resolved, so there is no size to
 * publish. The drop is what makes {@link UnknownMemoryGraph} count-free at the
 * type level, and it costs no information — every surface renders the unknown
 * card in that state and never the network.
 */
function buildGraphEnvelope(
  aggregate: AggregateCoverage,
  network: {
    readonly nodes: readonly GraphNode[];
    readonly edges: readonly GraphEdge[];
    readonly clusters: readonly GraphCluster[];
    readonly truncated: boolean;
  },
): MemoryGraphData {
  if (aggregate.status === "unknown") {
    return {
      status: "unknown",
      reason: aggregate.reason,
      asOf: aggregate.asOf,
      coverage: aggregate.coverage,
    };
  }
  return {
    status: "ok",
    reason: null,
    asOf: aggregate.asOf,
    coverage: aggregate.coverage,
    nodes: network.nodes,
    edges: network.edges,
    clusters: network.clusters,
    truncated: network.truncated,
  };
}

/* ------------------------------------------------------------------------- *
 * Small deterministic helpers.
 * ------------------------------------------------------------------------- */

function compareStringAsc(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Runs `task` over `items` with at most `limit` calls in flight, returning the
 * results in **input order** regardless of completion order. This is the whole
 * basis for the graph load's determinism under concurrency: a phase fires its
 * independent per-entity requests through here, then folds the returned array —
 * indexed by the item's deterministic position — into the accumulator serially.
 * `next` is claimed synchronously (JS is single-threaded), so each index is
 * fetched exactly once. A rejection propagates (Promise.all), matching the
 * serial version's throw-on-transport-failure — a partial graph is never built.
 */
async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    for (let index = next++; index < items.length; index = next++) {
      results[index] = await task(items[index], index);
    }
  };
  const workerCount = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: workerCount }, worker));
  return results;
}

/* ------------------------------------------------------------------------- *
 * Graph assembly.
 * ------------------------------------------------------------------------- */

/**
 * Loads a bounded, deterministic Memory Graph view.
 *
 * `asOf` is the caller-supplied coverage cut-off (as OpenCommitmentsView passes
 * `new Date().toISOString()`); the returned `asOf` is the coverage watermark,
 * never a wall clock. Throws {@link ControlPlaneError} on transport/parse
 * failure — a partial graph is never fabricated.
 */
/**
 * The commitment-seeded map: entities and relations reachable from the actor's
 * open commitments.
 *
 * Not mounted while claim extraction is off. It bootstraps from
 * get_open_commitments, so with no commitments it seeds nothing and draws
 * nothing, and the scope switch that used to select it has been taken off the
 * toolbar rather than left as a position that is always empty. It is kept whole,
 * with its tests, because the switch returns when extraction does — see
 * `loadFullMemoryGraph`, which is the single view the page renders meanwhile.
 */
export async function loadMemoryGraph(
  apiBase: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<MemoryGraphData> {
  const coverage = new CoverageAccumulator();
  let requests = 0;
  let truncated = false;
  const canSpend = (): boolean => requests < MEMORY_GRAPH_MAX_REQUESTS;

  // Phase 1 — bootstrap: all open commitments (paged) seed the entity set and
  // supply the per-entity status badges.
  const commitments: OpenCommitment[] = [];
  let cursor: string | null = null;
  do {
    if (!canSpend()) {
      truncated = true;
      break;
    }
    requests += 1;
    const page = await getOpenCommitments(
      apiBase,
      { asOf, entityId: null, cursor },
      signal,
    );
    coverage.record(page.coverage, page.status, page.reason);
    for (const item of page.items) commitments.push(item);
    cursor = page.nextCursor;
  } while (cursor);

  const seedIds = new Set<string>();
  const commitmentsBySubject = new Map<string, OpenCommitment[]>();
  for (const commitment of commitments) {
    seedIds.add(commitment.subjectEntityId);
    if (commitment.counterpartyEntityId) {
      seedIds.add(commitment.counterpartyEntityId);
    }
    const owned = commitmentsBySubject.get(commitment.subjectEntityId) ?? [];
    owned.push(commitment);
    commitmentsBySubject.set(commitment.subjectEntityId, owned);
  }

  // Phase 2 — resolve seed entities (id asc). Only successfully fetched
  // entities become nodes; an unresolved entity is dropped (fail-closed). A seed
  // whose own claim is withheld (unknown/no_authorized_evidence under complete
  // coverage) is an honest empty and must not flip the whole view to unknown.
  const entities = new Map<string, ParsedEntity>();
  {
    const sortedSeedIds = [...seedIds].sort(compareStringAsc);
    // The node cap is what bounds this phase now: a batched read costs one round
    // trip per fifty ids, so the request budget cannot bind before it. A dropped
    // suffix is honest truncation, exactly as it was per-id.
    const allowed = Math.max(
      0,
      Math.min(sortedSeedIds.length, MEMORY_GRAPH_MAX_NODES - entities.size),
    );
    if (allowed < sortedSeedIds.length) truncated = true;
    const resolved = await resolveEntities(
      apiBase,
      sortedSeedIds.slice(0, allowed),
      asOf,
      signal,
    );
    requests += resolved.requests;
    // Fold in input (id asc) order: identical coverage.record sequence and
    // entity insertion order to the serial loop, so firstUnknown/asOf and the
    // whole result stay byte-for-byte deterministic under any arrival order.
    for (const envelope of resolved.envelopes) {
      const entityCoverage = honestEmptyCoverageStatus(envelope);
      coverage.record(
        envelope.coverage,
        entityCoverage.status,
        entityCoverage.reason,
      );
    }
    // A seed that could not be read at all is a gap in the map, recorded as
    // truncation rather than as a watermark.
    if (resolved.dropped) truncated = true;
    for (const entity of resolved.entities)
      entities.set(entity.entityId, entity);
  }

  // Phase 3 — one-hop relation traversal from each resolved entity, ordered by
  // (evidence count desc, id asc) so the request budget is spent on the most
  // evidenced entities first.
  const rawRelations: ParsedRelation[] = [];
  const neighborIds = new Set<string>();
  // The same relation row returns from both endpoints' traversals; collect it
  // once so merged edge weights never double-count evidence.
  const seenRelationIds = new Set<string>();
  const byEvidence = [...entities.values()].sort(
    (a, b) =>
      b.evidenceCount - a.evidenceCount ||
      compareStringAsc(a.entityId, b.entityId),
  );
  {
    const allowed = Math.max(
      0,
      Math.min(byEvidence.length, MEMORY_GRAPH_MAX_REQUESTS - requests),
    );
    if (allowed < byEvidence.length) truncated = true;
    const batch = byEvidence.slice(0, allowed);
    requests += batch.length;
    const results = await mapWithConcurrency(
      batch,
      MEMORY_GRAPH_FETCH_CONCURRENCY,
      (entity) => fetchRelations(apiBase, entity.entityId, asOf, signal),
    );
    // Fold in input (evidence desc, id asc) order: identical relation dedup and
    // neighbor-discovery order to the serial loop.
    for (const result of results) {
      const relationsCoverage = honestEmptyCoverageStatus(result);
      coverage.record(
        result.coverage,
        relationsCoverage.status,
        relationsCoverage.reason,
      );
      if (result.nextCursor) truncated = true;
      for (const relation of result.relations) {
        if (seenRelationIds.has(relation.relationId)) continue;
        seenRelationIds.add(relation.relationId);
        rawRelations.push(relation);
        neighborIds.add(relation.fromEntityId);
        neighborIds.add(relation.toEntityId);
      }
    }
  }

  // Phase 4 — resolve newly discovered neighbors (id asc) for their labels. A
  // neighbor whose own claim is withheld (unknown/no_authorized_evidence under
  // complete coverage) is an honest empty: its node is dropped and buildEdges
  // discards the dangling edge, but the view stays ok rather than flipping.
  const newNeighbors = [...neighborIds]
    .filter((id) => !entities.has(id))
    .sort(compareStringAsc);
  {
    const allowed = Math.max(
      0,
      Math.min(newNeighbors.length, MEMORY_GRAPH_MAX_NODES - entities.size),
    );
    if (allowed < newNeighbors.length) truncated = true;
    const resolved = await resolveEntities(
      apiBase,
      newNeighbors.slice(0, allowed),
      asOf,
      signal,
    );
    requests += resolved.requests;
    for (const envelope of resolved.envelopes) {
      const entityCoverage = honestEmptyCoverageStatus(envelope);
      coverage.record(
        envelope.coverage,
        entityCoverage.status,
        entityCoverage.reason,
      );
    }
    // Same rule as the seed phase: a neighbour that could not be read is a
    // stated gap, and buildEdges discards the edge that pointed at it.
    if (resolved.dropped) truncated = true;
    for (const entity of resolved.entities)
      entities.set(entity.entityId, entity);
  }

  const edges = buildEdges(rawRelations, entities);
  const adjacency = buildAdjacency(edges);
  const clustering = deriveClusters(entities, edges, adjacency);
  // Fold coverage before the nodes are built: the badges are timed against the
  // watermark this very envelope reports, so one screen states one "now".
  const aggregate = coverage.build();
  const nodes = buildNodes(
    entities,
    adjacency,
    clustering,
    commitmentsBySubject,
    aggregate.asOf,
    aggregate.coverage.state === "complete",
  );

  return buildGraphEnvelope(aggregate, {
    nodes,
    edges,
    clusters: clustering.clusters,
    truncated,
  });
}

/**
 * Loads the full authorized "neuron network": every entity and relation the
 * caller is authorized to see, regardless of commitment linkage, enumerated via
 * list_entities / list_relations. The commitment graph (loadMemoryGraph) is left
 * untouched; this is the opt-in `scope: "full"` path.
 *
 * Honesty is identical to the commitment path. Open commitments are still read —
 * but only to derive each node's status badge, never to *seed* the graph. The
 * enumeration is bounded by {@link MEMORY_GRAPH_MAX_NODES} entities and
 * {@link MEMORY_GRAPH_MAX_EDGES} relations; whenever more authorized data exists
 * than the cap, `truncated` is set (surfaced as an honest "N+" scale line — never
 * a silent trim). Every record is re-validated fail-closed; a relation whose
 * endpoint is not among the resolved nodes is dropped by {@link buildEdges}, so
 * no phantom node or edge is ever drawn.
 */
interface EnumerationRead<Item> {
  readonly items: readonly Item[];
  readonly envelopes: readonly EnvelopeStatus[];
  /** True when the read stopped at its cap with authorized data still behind it. */
  readonly capped: boolean;
}

/** Walks the open-commitment cursor to its end; every page badges some node. */
async function readAllCommitments(
  apiBase: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<EnumerationRead<OpenCommitment>> {
  const items: OpenCommitment[] = [];
  const envelopes: EnvelopeStatus[] = [];
  let cursor: string | null = null;
  do {
    const page = await getOpenCommitments(
      apiBase,
      { asOf, entityId: null, cursor },
      signal,
    );
    envelopes.push({
      coverage: page.coverage,
      status: page.status,
      reason: page.reason,
    });
    for (const item of page.items) items.push(item);
    cursor = page.nextCursor;
  } while (cursor);
  return { items, envelopes, capped: false };
}

/** Walks list_entities (id asc) up to {@link MEMORY_GRAPH_MAX_NODES}. */
async function readAllEntities(
  apiBase: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<EnumerationRead<ParsedEntity>> {
  const items: ParsedEntity[] = [];
  const envelopes: EnvelopeStatus[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  let capped = false;
  do {
    const page = await fetchEntitiesPage(apiBase, asOf, cursor, signal);
    envelopes.push({
      coverage: page.coverage,
      status: page.status,
      reason: page.reason,
    });
    for (const entity of page.entities) {
      if (seen.size >= MEMORY_GRAPH_MAX_NODES) {
        capped = true;
        break;
      }
      if (seen.has(entity.entityId)) continue;
      seen.add(entity.entityId);
      items.push(entity);
    }
    cursor = capped ? null : page.nextCursor;
  } while (cursor);
  return { items, envelopes, capped };
}

/**
 * Relation kinds this map does not draw.
 *
 * `committed_to` is a semantic edge: it exists only because a claim extractor
 * asserted it. The claim-backed rows were removed with the lane (Erkan,
 * 2026-08-03), so today this set matches nothing — no such relation is returned
 * to filter. It is kept deliberately, and not as dead weight: the day extraction
 * returns, edges of this kind start arriving again, and whether the map draws
 * them is a decision this screen has to make on purpose rather than inherit by
 * default. This is the gate that keeps that decision here.
 */
const UNDRAWN_RELATION_TYPES: ReadonlySet<RelationType> = new Set([
  "committed_to",
]);

/**
 * Walks list_relations (id asc) up to {@link MEMORY_GRAPH_MAX_EDGES}.
 *
 * The undrawn kinds are dropped here rather than after the walk, so the cap
 * counts what the map will actually draw: a page full of frozen semantic edges
 * must not consume the budget of edges the reader can see.
 */
async function readAllRelations(
  apiBase: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<EnumerationRead<ParsedRelation>> {
  const items: ParsedRelation[] = [];
  const envelopes: EnvelopeStatus[] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  let capped = false;
  do {
    const page = await fetchRelationsPage(apiBase, asOf, cursor, signal);
    envelopes.push({
      coverage: page.coverage,
      status: page.status,
      reason: page.reason,
    });
    for (const relation of page.relations) {
      if (items.length >= MEMORY_GRAPH_MAX_EDGES) {
        capped = true;
        break;
      }
      if (seen.has(relation.relationId)) continue;
      seen.add(relation.relationId);
      if (
        relation.relationType !== null &&
        UNDRAWN_RELATION_TYPES.has(relation.relationType)
      ) {
        continue;
      }
      items.push(relation);
    }
    cursor = capped ? null : page.nextCursor;
  } while (cursor);
  return { items, envelopes, capped };
}

export async function loadFullMemoryGraph(
  apiBase: string,
  asOf: string,
  signal?: AbortSignal,
): Promise<MemoryGraphData> {
  const coverage = new CoverageAccumulator();

  // The three reads are independent — commitments only badge nodes, and an edge
  // whose endpoint is missing is dropped afterwards either way — so they run
  // together instead of one keyset chain after another. Each chain is still
  // strictly serial in itself (a cursor is only issued by the page before it);
  // what overlaps is the three chains, turning 1 + 2 + 9 round trips on the live
  // tenant into the longest of them. Coverage is folded below in a fixed order,
  // so the watermark and the unknown status do not depend on which lands first.
  const [commitmentRead, entityRead, relationRead] = await Promise.all([
    readAllCommitments(apiBase, asOf, signal),
    readAllEntities(apiBase, asOf, signal),
    readAllRelations(apiBase, asOf, signal),
  ]);

  for (const envelope of commitmentRead.envelopes) {
    coverage.record(envelope.coverage, envelope.status, envelope.reason);
  }
  const commitmentsBySubject = new Map<string, OpenCommitment[]>();
  for (const commitment of commitmentRead.items) {
    const owned = commitmentsBySubject.get(commitment.subjectEntityId) ?? [];
    owned.push(commitment);
    commitmentsBySubject.set(commitment.subjectEntityId, owned);
  }

  const entities = new Map<string, ParsedEntity>();
  for (const envelope of entityRead.envelopes) {
    const folded = honestEmptyCoverageStatus(envelope);
    coverage.record(envelope.coverage, folded.status, folded.reason);
  }
  for (const entity of entityRead.items) entities.set(entity.entityId, entity);

  const rawRelations: ParsedRelation[] = [];
  for (const envelope of relationRead.envelopes) {
    const folded = honestEmptyCoverageStatus(envelope);
    coverage.record(envelope.coverage, folded.status, folded.reason);
  }
  for (const relation of relationRead.items) rawRelations.push(relation);

  const truncated = entityRead.capped || relationRead.capped;
  const edges = buildEdges(rawRelations, entities);
  const adjacency = buildAdjacency(edges);
  const clustering = deriveClusters(entities, edges, adjacency);
  // Same rule as the commitment path: badges are timed against the reported
  // watermark, never the wall clock the caller queried with.
  const aggregate = coverage.build();
  const nodes = buildNodes(
    entities,
    adjacency,
    clustering,
    commitmentsBySubject,
    aggregate.asOf,
    aggregate.coverage.state === "complete",
  );

  return buildGraphEnvelope(aggregate, {
    nodes,
    edges,
    clusters: clustering.clusters,
    truncated,
  });
}

/**
 * How strongly the record ties this pair, from the stored bond strengths, or
 * null when it cannot be said from them alone.
 *
 * A stated strength counts distinct published documents for one *directed*
 * triple — subject, object, relation kind — so every relation row of that
 * triple repeats the same number. Summing the bucket row by row would multiply
 * a bond by how many times it was extracted; summing one value per distinct
 * triple is the pair's real total.
 *
 * Null the moment any relation in the bucket states none. Falling back whole is
 * the honest move: a weight that is part document count and part evidence count
 * is a number about nothing, and this map's thickness is a claim about how much
 * two people actually deal with each other.
 */
function bondStrengthOf(bucket: readonly ParsedRelation[]): number | null {
  const byTriple = new Map<string, number>();
  for (const relation of bucket) {
    if (relation.observationCount === undefined) return null;
    byTriple.set(
      JSON.stringify([
        relation.fromEntityId,
        relation.toEntityId,
        relation.relationType,
      ]),
      relation.observationCount,
    );
  }
  if (byTriple.size === 0) return null;
  let total = 0;
  for (const count of byTriple.values()) total += count;
  return total;
}

/**
 * Merges parallel relations into at most one undirected edge per node pair. Both
 * endpoints must be resolved authorized nodes, or the edge is dropped — a
 * relation to an unauthorized entity leaks no phantom node or edge. Weight is
 * the pair's bond strength when the API states one for every relation in it and
 * its total evidence (min 1) otherwise — see {@link bondStrengthOf}; the merged
 * relation type is the highest evidenced relation's type (tie-break relationId
 * asc); direction follows that dominant relation.
 */
function buildEdges(
  relations: readonly ParsedRelation[],
  entities: ReadonlyMap<string, ParsedEntity>,
): readonly GraphEdge[] {
  const groups = new Map<string, ParsedRelation[]>();
  for (const relation of relations) {
    // Not a silent skip: `app_private.relations` carries
    // `check (subject_entity_id <> object_entity_id)`, so a self-relation is a
    // state the record cannot hold. This is the reader refusing to draw a
    // degenerate zero-length curve for a response that contradicts the schema,
    // not a class of real relation being dropped unannounced.
    if (relation.fromEntityId === relation.toEntityId) continue;
    if (
      !entities.has(relation.fromEntityId) ||
      !entities.has(relation.toEntityId)
    ) {
      continue;
    }
    const [low, high] =
      relation.fromEntityId < relation.toEntityId
        ? [relation.fromEntityId, relation.toEntityId]
        : [relation.toEntityId, relation.fromEntityId];
    const key = JSON.stringify([low, high]);
    const bucket = groups.get(key) ?? [];
    bucket.push(relation);
    groups.set(key, bucket);
  }

  const edges: GraphEdge[] = [];
  for (const bucket of groups.values()) {
    const dominant = [...bucket].sort(
      (a, b) =>
        // A name this build can read outranks one it cannot, ahead of evidence.
        // The merged edge carries exactly one relation's name, and letting an
        // unnameable relation win on weight would erase a kind the record does
        // state — trading one silence for another. Below that the old rule is
        // untouched, so a bucket of nameable relations orders exactly as before.
        Number(b.relationType !== null) - Number(a.relationType !== null) ||
        b.evidenceCount - a.evidenceCount ||
        compareStringAsc(a.relationId, b.relationId),
    )[0];
    if (!dominant) continue;
    const totalEvidence = bucket.reduce(
      (sum, relation) => sum + relation.evidenceCount,
      0,
    );
    edges.push({
      sourceNodeId: dominant.fromEntityId,
      targetNodeId: dominant.toEntityId,
      relationType: dominant.relationType,
      weight: bondStrengthOf(bucket) ?? Math.max(1, totalEvidence),
      // Every edge here is a real, evidenced relation — there are no synthetic
      // thin hub links in the data layer — so it is always drawn strong, name or
      // no name. Thinning an edge this build merely cannot name would say the
      // link is weaker than the record states, which is the opposite of true.
      strong: true,
      ...statesOf(bucket),
    });
  }

  edges.sort(
    (a, b) =>
      compareStringAsc(a.sourceNodeId, b.sourceNodeId) ||
      compareStringAsc(a.targetNodeId, b.targetNodeId),
  );
  return edges;
}

/**
 * The distinct assertion states behind a merged edge, in the frozen vocabulary's
 * own order.
 *
 * A set rather than one winning value, because an edge merges parallel
 * relations and picking a single "worst" state here would answer a question the
 * record never asked — two relations between the same pair can disagree, and
 * that disagreement is information. The view decides what to draw; this layer
 * only reports what was stated.
 *
 * The key is omitted when no relation in the bucket stated a state at all. An
 * empty array would read as "these relations have no states", which is a claim;
 * absent reads as "nobody said", which is the truth. A bucket where only some
 * relations stated one reports those: "at least one is conflicted" is honest
 * even when the rest are silent.
 */
function statesOf(bucket: readonly ParsedRelation[]): {
  states?: readonly ClaimState[];
} {
  const present = new Set<ClaimState>();
  for (const relation of bucket) {
    if (relation.state) present.add(relation.state);
  }
  if (present.size === 0) return {};
  return { states: claimStates.filter((state) => present.has(state)) };
}

/** Undirected adjacency: id → (neighbor id → merged edge weight). */
function buildAdjacency(
  edges: readonly GraphEdge[],
): Map<string, Map<string, number>> {
  const adjacency = new Map<string, Map<string, number>>();
  const link = (from: string, to: string, weight: number): void => {
    const neighbors = adjacency.get(from) ?? new Map<string, number>();
    neighbors.set(to, weight);
    adjacency.set(from, neighbors);
  };
  for (const edge of edges) {
    link(edge.sourceNodeId, edge.targetNodeId, edge.weight);
    link(edge.targetNodeId, edge.sourceNodeId, edge.weight);
  }
  return adjacency;
}

interface Clustering {
  readonly clusters: readonly GraphCluster[];
  readonly clusterIdByNode: ReadonlyMap<string, string>;
  readonly hubIds: ReadonlySet<string>;
}

/**
 * Deterministic, non-semantic clustering (README §3): degree-based hub
 * selection, strongest-edge member attachment, greedy inheritance for the rest,
 * isolated nodes as their own single-member cluster, and a non-semantic color
 * cycle over {@link CLUSTER_COLOR_ORDER}. Grouping carries no status meaning.
 */
function deriveClusters(
  entities: ReadonlyMap<string, ParsedEntity>,
  edges: readonly GraphEdge[],
  adjacency: ReadonlyMap<string, Map<string, number>>,
): Clustering {
  const ids = [...entities.keys()].sort(compareStringAsc);
  const degreeOf = (id: string): number => adjacency.get(id)?.size ?? 0;
  const evidenceOf = (id: string): number =>
    entities.get(id)?.evidenceCount ?? 0;

  // Hub selection: top-K by (degree desc, evidence desc, id asc), filtered to
  // those meeting the minimum hub degree so a sparse graph does not over-split.
  const clusterCount = clamp(
    Math.round(Math.sqrt(ids.length / 2)),
    1,
    MEMORY_GRAPH_MAX_CLUSTERS,
  );
  const byHubPriority = [...ids].sort(
    (a, b) =>
      degreeOf(b) - degreeOf(a) ||
      evidenceOf(b) - evidenceOf(a) ||
      compareStringAsc(a, b),
  );
  const hubIds = new Set(
    byHubPriority
      .slice(0, clusterCount)
      .filter((id) => degreeOf(id) >= CLUSTER_MIN_HUB_DEGREE),
  );

  const hubByNode = new Map<string, string>();
  for (const hub of hubIds) hubByNode.set(hub, hub);

  // Step A — attach each non-hub node to its strongest adjacent hub.
  for (const id of ids) {
    if (hubIds.has(id)) continue;
    let bestHub: string | null = null;
    let bestWeight = -Infinity;
    for (const [neighbor, weight] of adjacency.get(id) ?? []) {
      if (!hubIds.has(neighbor)) continue;
      if (
        weight > bestWeight ||
        (weight === bestWeight && (bestHub === null || neighbor < bestHub))
      ) {
        bestWeight = weight;
        bestHub = neighbor;
      }
    }
    if (bestHub) hubByNode.set(id, bestHub);
  }

  // Step B — greedy inheritance along strongest edges, deterministic order.
  const sortedEdges = [...edges].sort(
    (a, b) =>
      b.weight - a.weight ||
      compareStringAsc(a.sourceNodeId, b.sourceNodeId) ||
      compareStringAsc(a.targetNodeId, b.targetNodeId),
  );
  let changed = true;
  let guard = 0;
  while (changed && guard < ids.length) {
    changed = false;
    guard += 1;
    for (const edge of sortedEdges) {
      const uHas = hubByNode.has(edge.sourceNodeId);
      const vHas = hubByNode.has(edge.targetNodeId);
      if (uHas && !vHas) {
        hubByNode.set(edge.targetNodeId, hubByNode.get(edge.sourceNodeId)!);
        changed = true;
      } else if (vHas && !uHas) {
        hubByNode.set(edge.sourceNodeId, hubByNode.get(edge.targetNodeId)!);
        changed = true;
      }
    }
  }

  // Step C — anything still unassigned is its own single-member cluster.
  for (const id of ids) {
    if (!hubByNode.has(id)) hubByNode.set(id, id);
  }

  const members = new Map<string, string[]>();
  for (const id of ids) {
    const hub = hubByNode.get(id)!;
    const bucket = members.get(hub) ?? [];
    bucket.push(id);
    members.set(hub, bucket);
  }

  // Color assignment: clusters ordered by (hub degree desc, hub id asc) take
  // tokens cycling through the non-semantic palette.
  const orderedHubs = [...members.keys()].sort(
    (a, b) => degreeOf(b) - degreeOf(a) || compareStringAsc(a, b),
  );
  const clusters: GraphCluster[] = orderedHubs.map((hub, index) => {
    const token: ClusterColorToken =
      CLUSTER_COLOR_ORDER[index % CLUSTER_COLOR_ORDER.length] ?? "accent";
    return {
      id: `cluster:${hub}`,
      label: entities.get(hub)?.canonicalName ?? hub,
      hubNodeId: hub,
      memberNodeIds: members.get(hub) ?? [],
      colorToken: token,
    };
  });

  const clusterIdByNode = new Map<string, string>();
  for (const id of ids)
    clusterIdByNode.set(id, `cluster:${hubByNode.get(id)!}`);

  return { clusters, clusterIdByNode, hubIds };
}

function tierForDegree(degree: number): NodeSizeTier {
  if (degree >= NODE_SIZE_TIER_MIN_DEGREE.hub) return "hub";
  if (degree >= NODE_SIZE_TIER_MIN_DEGREE.major) return "major";
  if (degree >= NODE_SIZE_TIER_MIN_DEGREE.regular) return "regular";
  return "minor";
}

function buildNodes(
  entities: ReadonlyMap<string, ParsedEntity>,
  adjacency: ReadonlyMap<string, Map<string, number>>,
  clustering: Clustering,
  commitmentsBySubject: ReadonlyMap<string, OpenCommitment[]>,
  /** Folded coverage watermark — the same instant the as-of strip states. */
  watermark: string | null,
  coverageComplete: boolean,
): readonly GraphNode[] {
  const ids = [...entities.keys()].sort(compareStringAsc);
  return ids.map((id) => {
    const entity = entities.get(id)!;
    const degree = adjacency.get(id)?.size ?? 0;
    const isHub = clustering.hubIds.has(id);
    return {
      id,
      entityType: entity.entityType,
      label: entity.canonicalName,
      evidenceCount: entity.evidenceCount,
      linkCount: degree,
      sizeTier: isHub ? "hub" : tierForDegree(degree),
      clusterId: clustering.clusterIdByNode.get(id) ?? `cluster:${id}`,
      // The typed ops carry no "first seen in the latest sync" signal
      // (EntityRecord has no such timestamp), so this is never invented.
      isNew: false,
      ...(entity.status === undefined ? {} : { status: entity.status }),
      ...(entity.identifier === undefined
        ? {}
        : {
            identifier: entity.identifier,
            identifierCount: entity.identifierCount ?? 1,
          }),
      statusBadge: deriveBadge(
        commitmentsBySubject.get(id) ?? [],
        watermark,
        coverageComplete,
      ),
    };
  });
}

/**
 * Projects an entity's open commitments to its worst alert badge, or null.
 * An `unknown` commitment never yields a badge. An overdue (danger) badge is
 * only asserted when the server already marks it overdue or coverage is
 * complete (mirrors the P1 overdue definition). Worst wins: the most overdue
 * danger, else the soonest dueSoon warning; ties break by commitment id asc.
 *
 * `watermark` is the folded coverage watermark of the very envelopes this view
 * shows — deliberately NOT the wall clock the caller queried with. Lateness is a
 * claim about a window, and Obsina has only read up to the watermark: between it
 * and "now" a "delivered" or "closed" message may sit unread, so calling a
 * commitment late against the wall clock asserts something about a stretch of
 * time nobody has looked at. It would also put two different "nows" on one
 * screen, since the as-of line beside the badge states the watermark. Without a
 * watermark there is no window at all, so no badge is derived (fail closed).
 */
function deriveBadge(
  commitments: readonly OpenCommitment[],
  watermark: string | null,
  coverageComplete: boolean,
): NodeStatusBadge | null {
  const asOfMs = watermark === null ? Number.NaN : Date.parse(watermark);
  if (!Number.isFinite(asOfMs)) return null;

  let danger: { readonly days: number; readonly id: string } | null = null;
  let warning: {
    readonly dueMs: number;
    readonly dueAt: string;
    readonly id: string;
  } | null = null;

  for (const commitment of commitments) {
    if (commitment.status === "unknown" || !commitment.dueAt) continue;
    const dueMs = Date.parse(commitment.dueAt);
    if (!Number.isFinite(dueMs)) continue;

    if (dueMs < asOfMs) {
      if (!coverageComplete && commitment.status !== "overdue") continue;
      const days = Math.floor((asOfMs - dueMs) / DAY_MS);
      if (
        !danger ||
        days > danger.days ||
        (days === danger.days && commitment.commitmentId < danger.id)
      ) {
        danger = { days, id: commitment.commitmentId };
      }
    } else if ((dueMs - asOfMs) / DAY_MS <= NODE_STATUS_DUE_SOON_DAYS) {
      if (
        !warning ||
        dueMs < warning.dueMs ||
        (dueMs === warning.dueMs && commitment.commitmentId < warning.id)
      ) {
        warning = {
          dueMs,
          dueAt: commitment.dueAt,
          id: commitment.commitmentId,
        };
      }
    }
  }

  if (danger)
    return { tone: "danger", kind: "overdue", overdueDays: danger.days };
  if (warning)
    return { tone: "warning", kind: "dueSoon", dueAt: warning.dueAt };
  return null;
}

/* ------------------------------------------------------------------------- *
 * Inspector.
 * ------------------------------------------------------------------------- */

/**
 * Builds the focused-entity inspector model from get_entity + get_open_commitments
 * (open items) + get_evidence (a bounded first page of evidence rows). Throws
 * {@link ControlPlaneError} when the entity itself cannot be resolved — a dead
 * inspector is never fabricated.
 *
 * There is deliberately no per-entity `get_relations` here. It used to run
 * alongside the other two reads and its whole product was a `linkCount` that no
 * surface renders: the panel's "who is this linked to" section is read off the
 * resolved graph the canvas already drew (see `MemoryGraphView.neighbours`), so
 * a second traversal of the same relations answered a question nobody asked.
 * Measured on the e2e harness at a fixed 300 ms per request, opening one entity
 * cost four knowledge requests; three of them decided what is on screen.
 *
 * Dropping it also drops its coverage envelope from this model's watermark, and
 * that is the accurate reading rather than a loosened one: nothing this panel
 * renders comes from that read any more. The neighbours' own coverage travels
 * with the graph envelope that produced them and is stated on the map's own
 * as-of strip.
 *
 * `onEarly` receives the same model one round trip sooner, holding everything
 * except the evidence page — which is the one part that cannot be read until
 * `get_entity` has said which excerpts exist. The promises a reader came for do
 * not have to wait behind that second trip, and the model says plainly that the
 * excerpts are still coming (`evidencePending`) so an unfinished read is never
 * drawn as "there is nothing here".
 */
export async function buildInspectorModel(
  apiBase: string,
  entityId: string,
  asOf: string,
  signal?: AbortSignal,
  onEarly?: (model: InspectorModel) => void,
): Promise<InspectorModel> {
  const coverage = new CoverageAccumulator();

  // The evidence page needs ids only `get_entity` carries, so it is chained onto
  // that read rather than awaited after the whole batch: the open promises are
  // independent and travel beside the pair instead of in front of it. The panel
  // therefore waits for `entity → evidence`, not for `slowest-of-three → evidence`.
  // Coverage is still folded in a fixed order (entity, then commitments), so the
  // watermark and the unknown status do not depend on which response lands first.
  const entityRead = fetchEntity(apiBase, entityId, asOf, signal);
  const evidenceRead = entityRead.then((result) =>
    result.entity && result.entity.evidenceIds.length > 0
      ? getCommitmentEvidence(
          apiBase,
          { asOf, evidenceIds: result.entity.evidenceIds },
          signal,
        )
      : null,
  );
  // The chain is awaited below, but only if the batch beneath it resolves. When
  // the caller aborts, `entityRead` rejects and takes this with it before
  // anything awaits it — an unhandled rejection the browser reports as a page
  // error. Marking it handled here changes nothing about the await: the promise
  // stays rejected and still throws where it is read.
  evidenceRead.catch(() => undefined);
  const [entityResult, commitmentsResult] = await Promise.all([
    entityRead,
    getOpenCommitments(apiBase, { asOf, entityId }, signal),
  ]);

  coverage.record(
    entityResult.coverage,
    entityResult.status,
    entityResult.reason,
  );
  const entity = entityResult.entity;
  if (!entity) {
    throw new ControlPlaneError("The knowledge surface returned no entity.");
  }

  coverage.record(
    commitmentsResult.coverage,
    commitmentsResult.status,
    commitmentsResult.reason,
  );
  // An overdue badge is a claim about a window nobody may have finished reading,
  // so it needs the coverage to be complete — not merely answerable. A partial
  // answer still lists its commitments; it just does not upgrade one to overdue
  // unless the server itself already marked it so.
  const coverageComplete =
    commitmentsResult.status === "ok" &&
    commitmentsResult.coverage.state === "complete";
  // Every envelope that moves the watermark has been recorded, so fold it here:
  // the badges are timed against the same instant the panel's own as-of line
  // states, rather than against the wall clock this read was queried with.
  const aggregate = coverage.build();
  const openItems: InspectorOpenItem[] = commitmentsResult.items.map(
    (commitment) => ({
      text: commitment.summary,
      ownerLabel: commitment.subjectEntityName,
      // Carried through as the record states it, null included: the panel asks
      // who promised whom, and the honest answer to the second half is
      // sometimes "the record does not say".
      counterpartyLabel: commitment.counterpartyEntityName,
      dueDate: commitment.dueAt,
      commitmentStatus: commitment.status,
      badge: deriveBadge([commitment], aggregate.asOf, coverageComplete),
    }),
  );

  const base = {
    entityId,
    asOf: aggregate.asOf,
    name: entity.canonicalName,
    entityType: entity.entityType,
    evidenceCount: entity.evidenceCount,
    openItems,
    viewAllCount: entity.evidenceCount,
    evidenceTruncated: entity.evidenceTruncated,
  } as const;

  // Everything but the excerpts, handed over now rather than in a round trip's
  // time. `evidencePending` is what keeps this honest: the section states that
  // it is still reading instead of standing empty, and no count is derived from
  // a page that has not arrived.
  if (onEarly && entity.evidenceIds.length > 0) {
    onEarly({ ...base, evidenceRows: [], evidencePending: true });
  }

  // Evidence is a bounded supplementary page; get_evidence carries no coverage
  // through the read-only projection, so it does not move the asOf watermark.
  const evidenceResult = await evidenceRead;
  const evidenceRows: InspectorEvidenceRow[] = (
    evidenceResult?.items ?? []
  ).map((item) => ({
    quote: item.quote,
    occurredAt: item.occurredAt,
    kind: deriveEvidenceKind(item),
    sourceChannel: item.channel,
    evidenceId: item.evidenceId,
    isHeaderLine: isEmailHeaderLine(item.quote),
    isDerived: item.layerKind === "ocr_derived",
  }));

  return { ...base, evidenceRows, evidencePending: false };
}

const EVIDENCE_KIND_HINTS: readonly (readonly [string, EvidenceKind])[] = [
  ["invoice", "invoice"],
  ["quote", "quote"],
  ["order", "order"],
  ["contract", "contract"],
  ["email", "mail"],
  ["mail", "mail"],
  ["document", "document"],
];

/**
 * Best-effort evidence classification from the locator (types.ts §7): scans the
 * locator's keys and string values for a recognizable kind token, else falls
 * back to `source`. Never invented to imply a stronger claim.
 */
function deriveEvidenceKind(evidence: CommitmentEvidence): EvidenceKind {
  const haystack: string[] = [];
  for (const [key, value] of Object.entries(evidence.locator)) {
    haystack.push(key.toLowerCase());
    if (typeof value === "string") haystack.push(value.toLowerCase());
  }
  for (const token of haystack) {
    for (const [needle, kind] of EVIDENCE_KIND_HINTS) {
      if (token.includes(needle)) return kind;
    }
  }
  return "source";
}

/**
 * Matches an email header line the extractor may have captured verbatim as an
 * evidence quote (e.g. "To: …", "- Subject: …", "> From: …"). Used only to add a
 * presentational "header line" label in the inspector — the quote itself is
 * always shown verbatim, never rewritten. A miss simply leaves the quote
 * unlabeled; it never hides or alters evidence.
 */
const EMAIL_HEADER_LINE =
  /^\s*[-–—>*]*\s*(to|from|cc|bcc|subject|date|sent|reply-to)\s*:/i;

function isEmailHeaderLine(quote: string): boolean {
  return EMAIL_HEADER_LINE.test(quote);
}

/* ------------------------------------------------------------------------- *
 * Narrow (320px) cluster-list fallback.
 * ------------------------------------------------------------------------- */

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * Builds the narrow-screen cluster cards from an assembled graph. The vignette
 * dot positions are a deterministic golden-angle scatter derived from member
 * order (hub centered), so this needs no layout dependency on G2.
 *
 * Takes the resolved variant only: cluster cards state entity and evidence
 * counts, and an unresolved answer has none to state.
 */
export function buildNarrowClusterCards(
  data: ResolvedMemoryGraph,
): readonly NarrowClusterCard[] {
  const nodeById = new Map(data.nodes.map((node) => [node.id, node] as const));
  return data.clusters.map((cluster) => {
    // Narrowing, not filtering: `deriveClusters` draws its membership from the
    // very entity map `buildNodes` turns into nodes, so every member id has a
    // node. The guard exists to satisfy the type, and cannot drop a card's
    // entity in any state the loaders above can produce.
    const memberNodes = cluster.memberNodeIds
      .map((id) => nodeById.get(id))
      .filter((node): node is GraphNode => node !== undefined);
    const evidenceCount = memberNodes.reduce(
      (sum, node) => sum + node.evidenceCount,
      0,
    );
    const entities: NarrowClusterEntity[] = memberNodes.map((node) => ({
      nodeId: node.id,
      name: node.label,
      entityType: node.entityType,
      linkCount: node.linkCount,
      statusBadge: node.statusBadge,
    }));
    return {
      clusterId: cluster.id,
      label: cluster.label,
      entityCount: memberNodes.length,
      evidenceCount,
      colorToken: cluster.colorToken,
      vignette: buildVignette(memberNodes, cluster.hubNodeId),
      entities,
    };
  });
}

function buildVignette(
  memberNodes: readonly GraphNode[],
  hubNodeId: string,
): readonly NarrowVignetteDot[] {
  const ordered = [...memberNodes].sort(
    (a, b) =>
      (a.id === hubNodeId ? 0 : 1) - (b.id === hubNodeId ? 0 : 1) ||
      compareStringAsc(a.id, b.id),
  );
  const count = ordered.length;
  return ordered.map((node, index) => {
    const isHub = node.id === hubNodeId;
    if (isHub) {
      return { position: { x: 0.5, y: 0.5 }, sizeTier: node.sizeTier, isHub };
    }
    const radius = 0.12 + 0.32 * Math.sqrt(count <= 1 ? 0 : index / count);
    const angle = index * GOLDEN_ANGLE;
    return {
      position: {
        x: clamp(0.5 + radius * Math.cos(angle), 0, 1),
        y: clamp(0.5 + radius * Math.sin(angle), 0, 1),
      },
      sizeTier: node.sizeTier,
      isHub,
    };
  });
}
